import { useCallback, useEffect, useRef, useState } from 'react';

import {
  AUTH_MOCK,
  GOOGLE_CLIENT_ID,
  GOOGLE_SCOPES,
  isEmailAllowed,
} from './config';
import { monitorApi } from '../api/monitor';
import { loadGoogleIdentity } from './gsi';
import {
  SESSION_REJECTED_EVENT,
  SESSION_RENEWED_EVENT,
  clearSession,
  readSession,
  writeSession,
} from './session';
import { fetchUserInfo } from './userinfo';
import type { AuthSession, GoogleAccounts, GoogleTokenClient, GoogleTokenResponse } from './types';

export type AuthStatus = 'idle' | 'signing-in' | 'authenticated';

export interface GoogleAuth {
  status: AuthStatus;
  session: AuthSession | null;
  error: string | null;
  /** Erro de configuração (client id ausente). */
  configError: string | null;
  signIn: () => void;
  signOut: () => void;
}

function describeTokenError(response: GoogleTokenResponse): string {
  if (response.error === 'access_denied') {
    return 'Permissão negada na conta Google.';
  }
  return response.error_description || 'Não foi possível concluir o login com o Google.';
}

export function useGoogleAuth(): GoogleAuth {
  const [session, setSession] = useState<AuthSession | null>(() => readSession());
  const [status, setStatus] = useState<AuthStatus>(() =>
    readSession() ? 'authenticated' : 'idle'
  );
  const [error, setError] = useState<string | null>(null);
  const tokenClient = useRef<GoogleTokenClient | null>(null);
  // Guardado para o `revoke` do logout sair pelo mesmo SDK que emitiu o token
  // (o do Google ou o falso), em vez de depender de `window.google`, que em
  // desenvolvimento não existe.
  const accounts = useRef<GoogleAccounts | null>(null);

  // O mock não usa client id: em desenvolvimento não há Google para configurar.
  const configError =
    !AUTH_MOCK && !GOOGLE_CLIENT_ID
      ? 'Login do Google não configurado no servidor (GOOGLE_CLIENT_ID). Avise a equipe de infraestrutura.'
      : null;

  const handleToken = useCallback(async (response: GoogleTokenResponse) => {
    if (response.error || !response.access_token) {
      setStatus('idle');
      setError(describeTokenError(response));
      return;
    }

    try {
      const profile = await fetchUserInfo(response.access_token);

      if (!profile.email) {
        throw new Error('A conta Google não retornou um e-mail.');
      }

      // Filtro de UX: a allowlist definitiva é checada no servidor.
      if (profile.email_verified === false || !isEmailAllowed(profile.email)) {
        accounts.current?.oauth2.revoke(response.access_token);
        setStatus('idle');
        setError(`A conta ${profile.email} não está autorizada a acessar o SICOP. Fale com o administrador.`);
        return;
      }

      // A troca de credencial: o token do Google entra, a sessão curta do
      // SICOP (15 min, registrada no banco) sai — é ela que as chamadas da
      // interface apresentam daqui em diante.
      const created = await monitorApi.createSession(response.access_token);

      const nextSession: AuthSession = {
        user: {
          email: profile.email,
          name: profile.name || profile.given_name || profile.email,
          picture: profile.picture,
        },
        accessToken: created.token,
        googleAccessToken: response.access_token,
        serverUser: created.user,
        expiresAt: Date.parse(created.expiresAt),
      };

      writeSession(nextSession);
      setSession(nextSession);
      setStatus('authenticated');
      setError(null);
    } catch (cause) {
      setStatus('idle');
      setError(
        cause instanceof Error
          ? `Falha ao validar a conta: ${cause.message}`
          : 'Falha ao validar a conta Google.'
      );
    }
  }, []);

  // Prepara o client do Google assim que o SDK estiver disponível.
  useEffect(() => {
    if (configError) return;

    let active = true;

    loadGoogleIdentity()
      .then((loaded) => {
        if (!active) return;
        accounts.current = loaded;
        tokenClient.current = loaded.oauth2.initTokenClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: GOOGLE_SCOPES,
          callback: (response) => void handleToken(response),
          error_callback: (cause) => {
            setStatus('idle');
            if (cause.type === 'popup_closed') {
              setError('Janela do Google fechada antes de concluir o login.');
              return;
            }
            setError(cause.message || 'Não foi possível abrir o login do Google.');
          },
        });
      })
      .catch((cause: Error) => {
        if (active) setError(cause.message);
      });

    return () => {
      active = false;
    };
  }, [configError, handleToken]);

  // A API recusou o token: volta para o login com o motivo que ela deu.
  useEffect(() => {
    const onRejected = (event: Event) => {
      setSession(null);
      setStatus('idle');
      setError((event as CustomEvent<string>).detail || 'Sessão expirada. Entre novamente.');
    };

    window.addEventListener(SESSION_REJECTED_EVENT, onRejected);
    return () => window.removeEventListener(SESSION_REJECTED_EVENT, onRejected);
  }, []);

  // Usar a tela renovou a sessão no servidor: o prazo novo já está no storage
  // (ver `renewSession`); recarregar o estado adia o timer de expiração junto.
  useEffect(() => {
    const onRenewed = () => {
      const renewed = readSession();
      if (renewed) setSession(renewed);
    };

    window.addEventListener(SESSION_RENEWED_EVENT, onRenewed);
    return () => window.removeEventListener(SESSION_RENEWED_EVENT, onRenewed);
  }, []);

  // Expira a sessão sozinha quando o token vence.
  useEffect(() => {
    if (!session) return;

    const timeout = window.setTimeout(
      () => {
        clearSession();
        setSession(null);
        setStatus('idle');
        setError('Sessão expirada. Entre novamente.');
      },
      Math.max(0, session.expiresAt - Date.now())
    );

    return () => window.clearTimeout(timeout);
  }, [session]);

  const signIn = useCallback(() => {
    if (configError) return;

    if (!tokenClient.current) {
      setError('SDK do Google ainda carregando. Tente novamente em instantes.');
      return;
    }

    setError(null);
    setStatus('signing-in');
    tokenClient.current.requestAccessToken({ prompt: 'select_account' });
  }, [configError]);

  const signOut = useCallback(() => {
    // Primeiro o servidor (a sessão morre no banco), depois o Google. Tudo
    // best-effort: sem rede, a sessão expira sozinha em minutos.
    void monitorApi.logout();
    const googleToken = session?.googleAccessToken;
    if (googleToken) accounts.current?.oauth2.revoke(googleToken);
    clearSession();
    setSession(null);
    setStatus('idle');
    setError(null);
  }, [session]);

  return { status, session, error, configError, signIn, signOut };
}
